import { PhoneSpecs, PhoneSpecsCondensed } from '../phones'
import { Answers, ScenarioType, Steps } from '../state/interfaces'
import { getPathname, generateQueryUrlFragment } from './endpointElementsGenerator'
import { getLocale } from '../i18n/locale'
import { getCookie, setCookie } from '../scenarios'
import { mapKeySearchCriterias, mapProvince, preparePostalCodeForUrl } from '../scenarios/utils'
import { sendGA4Event, sendGenerateLeadEvent } from '../analytics/ga4'
import { restoreScenarioInitialInformation } from '../state/effects/scenario'
import { getQueryParams } from './request'
import { getBaseUrl, isTheDomainProtegezVousWhitelabel } from '../whitelabels/whitelabel-domains'

const DEFAULT_PROVINCE = 'quebec'

const shouldAutoConfirmNewslettersForEmailPageGo = (scenarioType: Partial<ScenarioType>) =>
  !isTheDomainProtegezVousWhitelabel(window.location.hostname) &&
  (ScenarioType.EMAIL_PAGE_GO_INTERNET === scenarioType || ScenarioType.EMAIL_PAGE_GO_MOBILE === scenarioType)

export const getPhoneList = async (province: string): Promise<Array<PhoneSpecs>> => {
  const endpoint = `${process.env.REACT_APP_PHONES_API_URL}/${province}`
  const response = await fetch(endpoint)
  return (await response.json()).data
}

export const getPhoneListCondensed = async (): Promise<Array<PhoneSpecsCondensed>> => {
  const endpoint = `${process.env.REACT_APP_PHONES_SEARCH_API_URL}`
  const response = await fetch(endpoint)
  return (await response.json()).data
}

export const findAvailableDownloadSpeeds = async (provider: string): Promise<Array<number>> => {
  const endpoint = `${getBaseUrl()}/api/download-speed/internet/${provider}`
  const response = await fetch(endpoint)
  return (await response.json()).speeds
}

export interface CreateQuestionnaireRequest {
  answers: Answers
  scenarioType: string
  scenarioAdditionalInformation: Record<string, unknown>
  referrerUrl: string
  redirectionUrl: string
  searchText: string
}
export interface CreateQuestionnaireResponse {
  phoneModelId?: number
  redirectUrl: string
}

export const createQuestionnaire = async ({
  answers,
  scenarioType,
  scenarioAdditionalInformation,
  redirectionUrl,
  referrerUrl,
  searchText,
}: CreateQuestionnaireRequest): Promise<CreateQuestionnaireResponse> => {
  const scenariosWithNoResponse = [
    ScenarioType.MOBILE_REVIEWS,
    ScenarioType.INTERNET_REVIEWS,
    ScenarioType.MOBILE_REVIEWS_AWARD,
    ScenarioType.INTERNET_REVIEWS_AWARD,
    ScenarioType.LEAD_MOBILE,
    ScenarioType.LEAD_INTERNET,
    ScenarioType.NEWSLETTER,
    ScenarioType.FIBER_OPTIC_ALERT,
    ScenarioType.TICKET,
    ScenarioType.B2B,
    ScenarioType.COUNTRY,
    ScenarioType.EMAIL_COLLECTOR,
    ScenarioType.GOVERNMENT_EMPLOYEE,
  ]
  const baseEndpoint = getBaseUrl()

  if (scenariosWithNoResponse.includes(scenarioType as ScenarioType)) {
    if (scenarioType === ScenarioType.LEAD_MOBILE && !answers.hasOwnProperty('confirm-lead')) {
      const newProvince = getCookie('internetSearchProvince') || 'quebec'
      const newReferrerUrl = referrerUrl.replace(
        new RegExp(`${baseEndpoint}/(${getLocale()}/)?.+/compare-cellphone-plan`, 'i'),
        `${baseEndpoint}/${getLocale() === 'fr' ? getLocale() + '/' : ''}${newProvince}/compare-cellphone-plan`,
      )
      return {
        redirectUrl: newReferrerUrl,
      }
    }

    if (
      !(
        scenarioType === ScenarioType.LEAD_INTERNET &&
        scenarioAdditionalInformation?.providerSlug === 'bell' &&
        !answers.hasOwnProperty('confirm-lead')
      )
    ) {
      return {
        redirectUrl: '',
      }
    }
  }

  const queryUrlFragment = generateQueryUrlFragment(scenarioType, answers, scenarioAdditionalInformation, searchText)
  const pathname = getPathname(scenarioType)

  const province: string =
    getCookie(Steps.PROVINCE) || (answers[Steps.PROVINCE] ? answers[Steps.PROVINCE]![0] : DEFAULT_PROVINCE)

  if (scenarioType === ScenarioType.ADVISE_INTERNET) {
    return {
      redirectUrl: `${baseEndpoint}/${getLocale()}/${pathname}${queryUrlFragment}`,
    }
  }

  if (
    scenarioType === ScenarioType.EMAIL_PAGE_GO_INTERNET ||
    scenarioType === ScenarioType.EMAIL_PAGE_GO_MOBILE ||
    scenarioType === ScenarioType.CLIENT_INFO_INTERNET
  ) {
    let redirectionEndpoint = baseEndpoint

    if (scenarioType === ScenarioType.EMAIL_PAGE_GO_INTERNET || scenarioType === ScenarioType.EMAIL_PAGE_GO_MOBILE) {
      sendGA4Event('GA4_email_skip', {
        postal_code: getCookie('internetSearchPostalcode') || getCookie('geo-postal-code') || 'not set',
        region: answers['province']?.[0] || getCookie('province') || 'not set',
        locale: getLocale(),
        provider_name: getCookie('providerName') || 'not set',
        misc: answers['email-address']![0] === 'skipped' ? 'skip' : 'email',
      })
    }

    // handling local redirection
    if (baseEndpoint.match(/\/app_dev.php/)) {
      redirectionEndpoint = baseEndpoint.replace(/\/app_dev.php/, '')
    }

    if (scenarioType === ScenarioType.CLIENT_INFO_INTERNET && getQueryParams().providersng !== undefined) {
      return {
        redirectUrl: redirectionUrl,
      }
    }

    return {
      redirectUrl: `${redirectionEndpoint}${redirectionUrl}`,
    }
  }

  if (scenarioType === ScenarioType.PHONE_SEARCH) {
    return {
      redirectUrl: `${baseEndpoint}${getLocale() === 'fr' ? '/fr' : ''}/${pathname}${queryUrlFragment}`,
    }
  }

  return {
    redirectUrl: `${baseEndpoint}${getLocale() === 'fr' ? '/fr' : ''}/${province}/${pathname}${queryUrlFragment}`,
  }
}

const createReviewsBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  const city = getCookie('city') || getCookie('internetSearchLocality') || null

  const response: Record<string, string | number | null> = {
    address: answers['address-location'] ? answers['address-location']![0] : city ? city : null,
    country: getCookie('internetSearchCountry') || getCookie('country') || null,
    customerServiceRating: answers['bill']![0] || 0,
    email: answers['email-address']![0] || null,
    ip: getCookie('ip') || null,
    isp: getCookie('isp') || null,
    locale: getLocale() || null,
    locality: getCookie('internetSearchLocality') || getCookie('city') || 'nc',
    'name-nickname': answers['name-nickname']![0] || null,
    placeId: getCookie('placeId') || null,
    postalcode: preparePostalCodeForUrl(getCookie('internetSearchPostalcode')) || 'nc',
    problemSolvingRating: answers['customer-service']![0] || 0,
    province: getCookie('internetSearchProvince') || getCookie('province') || DEFAULT_PROVINCE,
    provinceReview: getCookie('internetSearchProvince') || getCookie('province') || DEFAULT_PROVINCE,
    qualityServicesRating: answers['onboarding-experience']![0] || 0,
    recommended: answers['recommend-provider']![0] ? (answers['recommend-provider']![0] === 'yes' ? 1 : 0) : 0, // Not recommended par default (must not normally get there)
    screenResolution: `${window.screen.height} x ${window.screen.width}`,
    speedEfficiencyRating: answers['digital-experience']![0] || 0,
    streetName: getCookie('internetSearchStreetName') || null,
    streetNumber: getCookie('internetSearchStreetNumber') || null,
  }

  if (scenarioType === ScenarioType.MOBILE_REVIEWS || scenarioType === ScenarioType.MOBILE_REVIEWS_AWARD) {
    response.networkQualityRating = answers['network']![0] || 0
    response.providerMobile = getCookie('providerSlugReviews')
    if (!response.providerMobile) {
      response.providerMobile = answers['select-current-provider']![0] || null
    }
  } else if (scenarioType === ScenarioType.INTERNET_REVIEWS || scenarioType === ScenarioType.INTERNET_REVIEWS_AWARD) {
    response.internetQualityRating = answers['network']![0] || 0
    response.providerInternet = getCookie('providerSlugReviews')
    if (!response.providerInternet) {
      response.providerInternet = answers['select-current-provider']![0] || null
    }
  }

  if (answers['final-word']) {
    response.title = filterEmojies(answers['final-word']![0]) || null
    response.comment = filterEmojies(answers['final-word']![1]) || null
  } else if (answers['final-word-referral-code']) {
    response.title = filterEmojies(answers['final-word-referral-code']![0]) || null
    response.comment = filterEmojies(answers['final-word-referral-code']![1]) || null
    response.referralCode = filterEmojies(answers['final-word-referral-code']![2]) || null
  }

  if (answers['confirm-review']) {
    if (answers['confirm-review'].includes('confirm-newsletters')) {
      response['confirm-newsletters'] = 'yes'
    } else {
      response['confirm-newsletters'] = 'no'
    }

    if (answers['confirm-review'].includes('confirm-contest')) {
      response['confirm-contest'] = 'yes'
    } else {
      response['confirm-contest'] = 'no'
    }
  }

  return response
}

const filterEmojies = (text: string): string => {
  const regex = /[\uD83C-\uDBFF\uDC00-\uDFFF]+/g
  return text.replace(regex, '')
}
const completeResponseWithInitialInformation = (
  response: Record<string, unknown>,
  initialInformation: Record<string, unknown>,
): Record<string, unknown> => {
  if (initialInformation['csv']) {
    response['csv'] = initialInformation['csv']
  }

  response['options-alert'] = 'Cable, DSL/FTTN, FTTH, Wireless, Satellite'

  if (initialInformation['downloadSpeed']) {
    response['download-speed'] = initialInformation['downloadSpeed']
  }

  response['postal-code'] = getCookie('internetSearchPostalcode') || getCookie('geo-postal-code') || 'not set'

  if (initialInformation['planDataUsage']) {
    response['monthly-usage'] = initialInformation['planDataUsage']
  }

  if (initialInformation['sr_voice']) {
    response['how-many-calling-minutes'] = initialInformation['sr_voice']
  }

  if (initialInformation['sc_data']) {
    response['how-much-data'] = initialInformation['sc_data']
  } else if (initialInformation['sr_data']) {
    response['how-much-data'] = initialInformation['sr_data']
  }

  if (initialInformation['sc_phone']) {
    response['phone-model-id'] = initialInformation['sc_phone']
  }

  return response
}

const createEmailCollectorBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  const response: Record<string, unknown> = {}
  console.log(scenarioType)

  for (const answer in answers) {
    let key = answer as Steps
    // to unify postal codes
    if (answer === Steps.EMAIL_ADDRESS_EMAIL_COLLECTOR) {
      key = 'email' as Steps
    }

    if (answer === Steps.CURRENT_PROVIDER_INTERNET_EMAIL_COLLECTOR) {
      key = 'currentInternetProvider' as Steps
    }

    if (answer === Steps.CURRENT_PROVIDER_MOBILE_EMAIL_COLLECTOR) {
      key = 'currentMobileProvider' as Steps
    }

    response[key] = answers[answer as Steps]!.join(';')
  }

  response['locale'] = getLocale()
  response['postalCode'] = getCookie('internetSearchPostalcode') || getCookie('geo-postal-code') || 'nc'

  return response
}

const createAlertBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  let response: Record<string, unknown> = {}

  for (const answer in answers) {
    let key = answer as Steps
    // to unify postal codes
    if (answer === Steps.POSTAL_CODE_INTERNET) {
      key = 'postal-code' as Steps
    }

    if (answer === Steps.INTERNET_SPEED) {
      key = 'download-speed' as Steps
    }

    if (answer === Steps.BIG_ENTREPRISE) {
      key = 'is_government_employee' as Steps
    }

    response[key] = answers[answer as Steps]!.join(';')
  }

  if (ScenarioType.EMAIL_PAGE_GO_INTERNET === scenarioType || ScenarioType.EMAIL_PAGE_GO_MOBILE === scenarioType) {
    if (getCookie('iigps')) {
      // initial info go page scenario
      const initialInfoGoPage = JSON.parse(atob(getCookie('iigps')))
      const restoredScenarioInitialInformation = restoreScenarioInitialInformation(initialInfoGoPage)
      response = completeResponseWithInitialInformation(response, restoredScenarioInitialInformation)
    }
  }

  // TODO: Find a way to make this more robust. Ex: Answers...[3] could change its value someday
  if (answers['address-location']) {
    response['locality'] = getCookie('internetSearchLocality') || answers['address-location']![3] || null
    response['country'] = getCookie('internetSearchCountry') || answers['address-location']![5] || null
    response['street-name'] = getCookie('internetSearchStreetName') || answers['address-location']![2] || null
    response['postal-code'] =
      preparePostalCodeForUrl(getCookie('internetSearchPostalcode')) ||
      preparePostalCodeForUrl(answers['address-location']![6]) ||
      null
    response['street-number'] = getCookie('internetSearchStreetNumber') || answers['address-location']![1] || null
    response['province'] =
      getCookie('internetSearchProvince') || mapProvince(answers['address-location']![4]) || DEFAULT_PROVINCE
  } else {
    response['province'] = getCookie('province') || DEFAULT_PROVINCE
  }
  response['locale'] = getLocale()
  response['url'] = getCookie('referrerUrl') || document.referrer || window.location.href

  if (answers['confirm-alert']) {
    if (
      answers['confirm-alert'].includes('confirm-newsletters') ||
      shouldAutoConfirmNewslettersForEmailPageGo(scenarioType)
    ) {
      response['confirm-newsletters'] = 'yes'
    } else {
      response['confirm-newsletters'] = 'no'
    }
  }

  if (scenarioType === ScenarioType.INTERNET || scenarioType === ScenarioType.INTERNET_ALERT) {
    response['monthly-usage'] = answers['monthly-usage']
      ? answers['monthly-usage'][0]
        ? answers['monthly-usage'][0]
        : 0
      : 0
    response['current-provider'] = getCookie('currentInternetProvider') ? getCookie('currentInternetProvider') : 'nc'
  }

  if (scenarioType === ScenarioType.EMAIL_PAGE_GO_MOBILE) {
    response['current-provider'] = getCookie('currentMobileProvider') ? getCookie('currentMobileProvider') : 'nc'
  }

  if (scenarioType === ScenarioType.EMAIL_PAGE_GO_INTERNET) {
    response['current-provider'] = getCookie('currentInternetProvider') ? getCookie('currentInternetProvider') : 'nc'
  }

  if (scenarioType === ScenarioType.MOBILE_ALERT_OBSERVER) {
    response['how-many-calling-minutes'] = 'unlimitedcalls'
  }

  if (response['postal-code']) {
    response['postal-code'] = preparePostalCodeForUrl(response['postal-code'] as string)
  }

  if (response['download-speed'] === 'custom-speed') {
    if (response['slider-data-amount']) {
      response['download-speed'] = response['slider-data-amount']
    }
  }

  if (response['how-much-data'] === 'other') {
    if (response['slider-data']) {
      response['how-much-data'] = response['slider-data']
    } else {
      response['how-much-data'] = '75000MB'
    }
  }

  const sortBy = getQueryParams().sortBy as string

  if (!sortBy || !['best', '2years'].includes(sortBy)) {
    response['sortBy'] = 'best'
  } else {
    response['sortBy'] = sortBy
  }

  return response
}

const createUserGovernmentEmployeeInfoBodyRequest = (
  answers: Answers,
  scenarioType: ScenarioType,
): Record<string, unknown> => {
  const response: Record<string, unknown> = {}
  console.log(scenarioType)

  for (const answer in answers) {
    const key = answer as Steps

    if (key == Steps.BIG_ENTREPRISE) {
      response['is_government_employee'] = answers[answer as Steps]![0] === 'yes' ? 'yes' : 'no'
    }

    if (key == Steps.EMAIL_ADDRESS) {
      response['email'] = answers[answer as Steps]![0] || null
    }
  }

  return response
}

const createFiberOpticAlertBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  const response: Record<string, unknown> = {}

  for (const answer in answers) {
    const key = answer as Steps
    response[key] = answers[answer as Steps]!.join(';')
  }

  if (answers['address-location']) {
    response['locality'] = getCookie('internetSearchLocality') || answers['address-location']![3] || null
    response['address'] = answers['address-location']![0] || null
    response['province'] =
      getCookie('internetSearchProvince') ||
      mapProvince(answers['address-location']![4]) ||
      getCookie('province') ||
      DEFAULT_PROVINCE
    response['postalCode'] =
      preparePostalCodeForUrl(getCookie('internetSearchPostalcode')) ||
      preparePostalCodeForUrl(answers['address-location']![6]) ||
      null
  } else {
    response['address'] = 'not set'
    response['province'] = getCookie('internetSearchProvince') || getCookie('province') || DEFAULT_PROVINCE
  }

  if (answers['email-address']) {
    response['email'] = answers['email-address']![0]
  }

  response['locale'] = getLocale()

  if (answers['confirm-alert']) {
    response['consent'] = answers['confirm-alert'].includes('confirm-alert') ? 'yes' : 'no'

    if (
      answers['confirm-alert'].includes('confirm-newsletters') ||
      shouldAutoConfirmNewslettersForEmailPageGo(scenarioType)
    ) {
      response['confirm-newsletters'] = 'yes'
    } else {
      response['confirm-newsletters'] = 'no'
    }
  }

  response['cityPage'] = getCookie('referrerUrl') || document.referrer || window.location.href

  const city = getQueryParams().city as string

  if (city) {
    response['city'] = city
  } else {
    response['city'] = getCookie('page-city') || getCookie('city') || 'not set'
  }

  return response
}

const createLeadBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  const excludedAnswers = [Steps.PROVIDER_AVAILABILITY, Steps.START_LEAD]

  for (const answer in answers) {
    if (!excludedAnswers.includes(answer as Steps)) {
      if (answer === Steps.CURRENT_PROVIDER_MOBILE_LEAD) {
        response[Steps.CURRENT_PROVIDER_MOBILE] = answers[answer as Steps]![0]
      } else {
        response[answer] = answers[answer as Steps]![0]
      }
    }
  }

  response['locale'] = getLocale()
  response['isDeal'] = answers['provider-availability']![1]
  response['email'] = answers['email-address-lead']![0]
  response['firstName'] = answers['first-name']![0]
  response['lastName'] = answers['last-name']![0]
  response['phone'] = answers['phone-number']![0]
  response['address'] = answers['address-location']![0]
  // response['locality'] = getCookie('internetSearchLocality') || answers['address-location']![3] || null
  // response['country'] = getCookie('internetSearchCountry') || answers['address-location']![5] || null
  // response['street-name'] = getCookie('internetSearchStreetName') || answers['address-location']![2] || null
  response['postalCode'] =
    preparePostalCodeForUrl(getCookie('internetSearchPostalcode')) ||
    preparePostalCodeForUrl(answers['address-location']![6]) ||
    null
  // response['street-number'] = getCookie('internetSearchStreetNumber') || answers['address-location']![1] || null
  response['province'] =
    getCookie('internetSearchProvince') || mapProvince(answers['address-location']![4]) || DEFAULT_PROVINCE
  response['placeId'] = getCookie('placeId') || null

  if (getCookie('scenario') && JSON.parse(getCookie('scenario')).initialInformation) {
    const additionalInformation: Record<string, unknown> = JSON.parse(
      getCookie('scenario').replace(/%3B/g, ';'),
    ).initialInformation
    for (const information in additionalInformation) {
      if (typeof additionalInformation[information] === 'object') {
        const informationAsString = JSON.stringify(additionalInformation[information])
        response[information] = informationAsString
      } else {
        response[information] = additionalInformation[information] as string
        if (information === 'providerSlug') {
          response['provider'] = additionalInformation[information] as string
        }
        if (information === 'bundle_status') {
          response['bundle_status'] = mapBundleStatus(additionalInformation[information]) as string
        }
      }
    }
  }
  return response
}

const createMektelLeadBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  const excludedAnswers = [Steps.PROVIDER_AVAILABILITY, Steps.START_LEAD, Steps.CONFIGURATION_TV]

  for (const answer in answers) {
    if (!excludedAnswers.includes(answer as Steps)) {
      if (answer === Steps.CURRENT_PROVIDER_MOBILE_LEAD) {
        response[Steps.CURRENT_PROVIDER_MOBILE] = answers[answer as Steps]![0]
      } else if (answer === Steps.SERVICE_TRANSFERRED) {
        response[answer] = answers[answer as Steps]?.join(';') || 'nc'
      } else {
        response[answer] = answers[answer as Steps]![0]
      }
    }
  }

  response['locale'] = getLocale()
  // response['isDeal'] = answers['provider-availability']![1]
  response['email'] = answers['email-address-lead']![0]
  response['firstName'] = answers['first-name']![0]
  response['lastName'] = answers['last-name']![0]
  response['phone'] = answers['phone-number']![0]
  response['address'] = answers['address-location']![0]
  // response['locality'] = getCookie('internetSearchLocality') || answers['address-location']![3] || null
  // response['country'] = getCookie('internetSearchCountry') || answers['address-location']![5] || null
  // response['street-name'] = getCookie('internetSearchStreetName') || answers['address-location']![2] || null
  response['postalCode'] =
    preparePostalCodeForUrl(getCookie('internetSearchPostalcode')) ||
    preparePostalCodeForUrl(answers['address-location']![6]) ||
    null
  // response['street-number'] = getCookie('internetSearchStreetNumber') || answers['address-location']![1] || null
  response['province'] =
    getCookie('internetSearchProvince') || mapProvince(answers['address-location']![4]) || DEFAULT_PROVINCE
  response['placeId'] = getCookie('placeId') || null

  if (getCookie('scenario') && JSON.parse(getCookie('scenario')).initialInformation) {
    const additionalInformation: Record<string, unknown> = JSON.parse(
      getCookie('scenario').replace(/%3B/g, ';'),
    ).initialInformation
    for (const information in additionalInformation) {
      if (typeof additionalInformation[information] === 'object') {
        const informationAsString = JSON.stringify(additionalInformation[information])
        response[information] = informationAsString
      } else {
        response[information] = additionalInformation[information] as string
        if (information === 'providerSlug') {
          response['provider'] = additionalInformation[information] as string
        }
        if (information === 'bundle_status') {
          response['bundle_status'] = mapBundleStatus(additionalInformation[information]) as string
        }
      }
    }
  }

  completeResponseWithNoAnswers(response)
  addMCToMektelRelatedQuestions(response)

  return response
}

const completeResponseWithNoAnswers = (response: Record<string, unknown>) => {
  const noAnswerKeys: Array<keyof Answers> = [
    Steps.CHOOSE_PHONE_PLAN,
    // Steps.CONFIGURATION_TV,
    Steps.CHOOSE_CHANNELS,
    Steps.SELECT_CURRENT_PROVIDER,
    Steps.SERVICE_TRANSFERRED,
    Steps.TRANSFER_INTERNET,
    Steps.PHONE_RESIDENTIAL,
    Steps.TV_NUMBER,
    Steps.TV_TYPE,
    Steps.KEEP_CURRENT_NUMBER,
    Steps.TECHNICIAN_TIME,
  ]

  noAnswerKeys.forEach((key) => {
    if (!response[key as string]) {
      response[key as string] = 'nc'
    }
  })
}

const addMCToMektelRelatedQuestions = (response: Record<string, unknown>) => {
  const cmMapHelper: Record<string, string> = {
    [Steps.CHOICE_INTERNET_MOBILE_TV]: 'chosen-additional-services',
    [Steps.CHOOSE_PHONE_PLAN]: 'chosen-phone-plan',
    [Steps.TV_TYPE]: 'type-of-tv',
    [Steps.TV_NUMBER]: 'number-of-TVs-to-connect',
    // [Steps.CONFIGURATION_TV]: 'chosen-tv-configuration',
    [Steps.CHOOSE_CHANNELS]: 'chosen-channels',
    [Steps.SERVICE_TRANSFERRED]: 'service-transferred',
    [Steps.TRANSFER_INTERNET]: 'current-technology',
    [Steps.TRANSFERRING_SERVICE]: 'transferring-service',
    [Steps.SELECT_CURRENT_PROVIDER]: 'current-provider',
    [Steps.PHONE_RESIDENTIAL]: 'phone-residential',
    [Steps.KEEP_CURRENT_NUMBER]: 'keep-current-number',
    [Steps.INSTALLATION_DATE_PICKER]: 'installation-date',
    [Steps.TECHNICIAN_TIME]: 'technician-time',
    [Steps.FINAL_WORD_MEKTEL]: 'comment',
  }

  Object.entries(cmMapHelper).forEach(([key, mappedKey]) => {
    if (response[key]) {
      response[`cm_${mappedKey}`] = response[key]
    }
  })

  const orderedCmEntries = Object.values(cmMapHelper)
    .map((mappedKey) => `cm_${mappedKey}`)
    .filter((cmKey) => Object.prototype.hasOwnProperty.call(response, cmKey))
    .map((cmKey) => [cmKey, response[cmKey]] as const)

  orderedCmEntries.forEach(([cmKey]) => {
    delete response[cmKey]
  })

  orderedCmEntries.forEach(([cmKey, value]) => {
    response[cmKey] = value
  })
}

const createTicketBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  response['locale'] = getLocale()
  response['formComments'] = answers['comment-ticket']![0]
  response['formEmail'] = answers['email-address-ticket']![0]
  response['formIssue'] = answers['ticket-initial-info']![0]
  response['formName'] = `${answers['first-name']![0]} ${answers['last-name']![0]}`
  response['urlPart'] = getCookie('referrerUrl') || document.referrer || window.location.href
  response['url'] = getCookie('referrerUrl') || document.referrer || window.location.href
  if (answers['comment-ticket']) {
    if (answers['comment-ticket'].includes('confirm-newsletters')) {
      response['confirm-newsletters'] = 'yes'
    } else {
      response['confirm-newsletters'] = 'no'
    }
  }

  if (getCookie('scenario') && JSON.parse(getCookie('scenario')).initialInformation) {
    const additionalInformation: Record<string, unknown> = JSON.parse(
      getCookie('scenario').replace(/%3B/g, ';'),
    ).initialInformation
    for (const information in additionalInformation) {
      if (typeof additionalInformation[information] === 'object') {
        const informationAsString = JSON.stringify(additionalInformation[information])
        response[information] = informationAsString
      } else {
        response[information] = additionalInformation[information] as string
        if (information === 'providerSlug') {
          response['provider'] = additionalInformation[information] as string
        }
      }
    }
  }
  return response
}

const createNewImmigrantBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  response['locale'] = getLocale()
  response['email'] = answers['email-address']![0]
  response['province'] = answers['province']![0]
  response['arriveAt'] = answers['date-picker']![0]
  response['confirm-newsletters'] = answers['confirm-visitor']![0] === 'confirm-newsletter' ? 'yes' : 'no'
  response['country'] = answers['country']![0]

  return response
}

const createNewUserUpdatedBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  response['email'] = answers['email-address']![0]
  response['nationality'] = answers['country']![0]

  return response
}

const createClientInfoBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  response['fbleads_PhoneNumber'] = answers['phone-number']?.[0] || answers['email-address']?.[2] || 'not set'
  response['fbleads_Name'] = answers['first-name']?.[0] || answers['email-address']?.[1] || 'not set'
  response['email'] = answers['email-address']![0] || 'not set'

  return response
}

const createB2BBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}

  response['email'] = answers['email-address']![0]
  response['firstname'] = answers['first-name']![0]
  response['lastname'] = answers['last-name']![0]
  response['phoneNumber'] = answers['phone-number']![0]
  response['companyname'] = answers['company-name']![0]
  response['zip'] = answers['postal-code']![0]
  response['address'] =
    answers['address-location'] && answers['address-location'][0] ? answers['address-location'][0] : ''
  response['lookingFor'] = answers['business-connectivity']![0]
  response['currentInternetProvider'] =
    answers['current-provider-internet-b2b'] && answers['current-provider-internet-b2b'][0]
      ? answers['current-provider-internet-b2b'][0]
      : ''
  response['currentMobileProvider'] =
    answers['current-provider-mobile-b2b'] && answers['current-provider-mobile-b2b'][0]
      ? answers['current-provider-mobile-b2b'][0]
      : ''
  response['internetSpeed'] =
    answers['internet-speed'] && answers['internet-speed'][0]
      ? answers['internet-speed'][0] !== 'custom-speed'
        ? answers['internet-speed'][0]
        : answers['slider-data-amount'] && answers['slider-data-amount'][0]
        ? answers['slider-data-amount'][0]
        : ''
      : ''
  response['howManyCellphoneLine'] =
    answers['how-many-cellphone-lines'] && answers['how-many-cellphone-lines'][0]
      ? answers['how-many-cellphone-lines'][0]
      : ''
  response['coverage'] =
    answers['canada-us-coverage'] && answers['canada-us-coverage'][0] ? answers['canada-us-coverage'][0] : ''
  response['specificComments'] =
    answers['final-word-b2b'] && answers['final-word-b2b'][0] ? answers['final-word-b2b']![0] : ''
  response['locale'] = getLocale()

  return response
}

const createConsentNewsletterBodyRequest = (answers: Answers, scenarioType: ScenarioType): Record<string, unknown> => {
  console.log(scenarioType)
  const response: Record<string, unknown> = {}
  response['locale'] = getLocale()
  response['email'] = answers['email-address']![0]
  response['province'] = answers['province'] ? answers['province']![0] : 'quebec'

  if (scenarioType === ScenarioType.MOBILE_REVIEWS_AWARD || scenarioType === ScenarioType.INTERNET_REVIEWS_AWARD) {
    response['confirm-newsletters'] =
      answers['confirm-review'] && answers['confirm-review'].includes('confirm-newsletters') ? 'yes' : 'no'
  } else {
    response['confirm-newsletters'] = answers['confirm-newsletter']![0]
  }

  return response
}

export const sendReviewsRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const mapScenarioTypeAndReviewsEndpoint: Partial<Record<Partial<ScenarioType>, string>> = {
    [ScenarioType.MOBILE_REVIEWS]: 'createMobileProviderReview',
    [ScenarioType.INTERNET_REVIEWS]: 'createInternetProviderReview',
    [ScenarioType.MOBILE_REVIEWS_AWARD]: 'createMobileProviderReview',
    [ScenarioType.INTERNET_REVIEWS_AWARD]: 'createInternetProviderReview',
  }

  const payload = new FormData()
  const payloadRawObject = createReviewsBodyRequest(answers, scenarioType)
  for (const i in payloadRawObject) {
    payload.append(i, payloadRawObject[i] as string)
  }

  const endpoint = `${getBaseUrl()}/${getLocale()}/${
    mapScenarioTypeAndReviewsEndpoint[scenarioType as Partial<ScenarioType>]
  }`

  await fetch(endpoint, {
    method: 'POST',
    body: payload,
    mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })

  const leadEventSource =
    scenarioType === ScenarioType.MOBILE_REVIEWS || scenarioType === ScenarioType.MOBILE_REVIEWS_AWARD
      ? 'mobile-review'
      : 'internet-review'

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, leadEventSource)
  }, 2000)
}

export const sendNewTicketRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const payloadRawObject = createTicketBodyRequest(answers, scenarioType)
  const endpoint = `${getBaseUrl()}/dyn/${getLocale()}/createIssue`

  await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, 'ticket')
  }, 2000)
}

export const sendNewImmigrantRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const payloadRawObject = createNewImmigrantBodyRequest(answers, scenarioType)
  const endpoint = `${getBaseUrl()}/api/customer/register-to-newsletter/immigrant`

  await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, 'new-immigrant')
  }, 2000)
}

// TODO: make less specific. Must work with nay update
export const sendNewUserUpdatedRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const payloadRawObject = createNewUserUpdatedBodyRequest(answers, scenarioType)
  const endpoint = `${getBaseUrl()}/api/customer/update`

  await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, 'new-user-country')
  }, 2000)
}

export const sendClientInfoRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const payloadRawObject = createClientInfoBodyRequest(answers, scenarioType)
  const endpoint = `${getBaseUrl()}/api/customer/save-attributes/generic`
  // const email = getEmailFromAnswers(answers)

  await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  // setTimeout(() => {
  //   identifyCioUser(email, 'client-info')
  // }, 2000)
}

export const sendB2BRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const payloadRawObject = createB2BBodyRequest(answers, scenarioType)
  const endpoint = `${getBaseUrl()}/api/customer/save-b-to-b`

  await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, 'btob')
  }, 2000)
}

export const sendConsentNewsletterRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const payloadRawObject = createConsentNewsletterBodyRequest(answers, scenarioType)
  const endpoint = `${getBaseUrl()}/api/customer/register-to-newsletter/consent`

  await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, 'newsletter-consent')
  }, 2000)
}

export const identifyCioUser = async (email: string | null, source: string | null = null) => {
  if (source) {
    sendGenerateLeadEvent({
      currency: 'CAD',
      value: 0,
      lead_source: source,
    })
  }

  try {
    const cioPhId = getCookie('cio_ph_id')
    const cioCookieParameter = cioPhId ? `&id=${cioPhId}` : ''

    const response = await fetch(`${getBaseUrl()}/api/customer/identify?dyn=1&email=${email}${cioCookieParameter}`)

    const jsonResponse = await response.json()

    const { cookies } = jsonResponse

    if (cookies) {
      cookies.forEach((cookie: { name: string; value: string }) => {
        if (cookie.name) setCookie(cookie.name, cookie.value, 365)
      })
    }
  } catch (error) {
    console.log('Some error occurred while identifying the user')
  }
}

export const sendNewsletterRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  console.log(scenarioType)
  const type = 'general'
  const email = answers['email-address']![0]
  const locale = getLocale()

  const endpoint = `${getBaseUrl()}/api/register-to-newsletter/${type}/${email}?locale=${locale}`

  await fetch(endpoint)

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, 'newsletter-general')
  }, 2000)
}

export const sendEmailCollectorRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  console.log(scenarioType)
  const emailCollectorEndpoint = `${getBaseUrl()}/api/add-user-to-mailer-segment`

  const payloadRawObject = createEmailCollectorBodyRequest(answers, scenarioType)

  const _response = await fetch(emailCollectorEndpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, 'email-collector')
  }, 2000)
}

let cancelRegister = false

export const addUserToEmailSegment = async (email: string) => {
  const endpoint = `${getBaseUrl()}/api/add-user-to-mailer-segment`

  const payloadRawObject = {
    email: email,
    sid: 208,
  }

  const _response = await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })

  cancelRegister = true
}

export const registerToNewsletter = async (email: string) => {
  const endpoint = `${getBaseUrl()}/api/customer/register-to-newsletter/consent`

  const payloadRawObject = {
    email: email,
    province: getCookie('province') || DEFAULT_PROVINCE,
    locale: getLocale(),
  }

  const _response = await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    // mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })
}

export const getSearchCriteriaObject = (
  nav: string | undefined,
  answers: Answers,
  additionalInformation: Record<string, unknown>,
) => {
  const mapSearchCriteria = mapKeySearchCriterias()

  if (nav === 'mobile') {
    return {
      [mapSearchCriteria.KEY_SEARCH_PHONE_MODEL]: answers['phone-model-id']
        ? answers['phone-model-id'][0]
        : additionalInformation['sr_phoneModel']
        ? additionalInformation['sr_phone']
        : 'not set',
      [mapSearchCriteria.KEY_SEARCH_DATA_AMOUNT]: answers['how-much-data']
        ? answers['how-much-data'][0]
        : additionalInformation['sr_data']
        ? additionalInformation['sr_data']
        : 'not set',
      [mapSearchCriteria.KEY_SEARCH_VOICE]: answers['how-many-calling-minutes']
        ? answers['how-many-calling-minutes'][0]
        : additionalInformation['sr_voice']
        ? additionalInformation['sr_voice']
        : 'not set',
      [mapSearchCriteria.KEY_SEARCH_OTHER_FILTERS]: additionalInformation['services'] || 'not set',
    }
  } else if (nav === 'internet') {
    return {
      [mapSearchCriteria.KEY_SEARCH_DOWNLOAD_SPEED]: answers['internet-speed']
        ? answers['internet-speed'][0]
        : additionalInformation['sr_downloadSpeed']
        ? additionalInformation['sr_downloadSpeed']
        : 'not set',
      [mapSearchCriteria.KEY_SEARCH_MONTHLY_USAGE]: answers['monthly-usage']
        ? answers['monthly-usage']
        : additionalInformation['planDataUsage']
        ? additionalInformation['planDataUsage']
        : 0,
      [mapSearchCriteria.KEY_SEARCH_OTHER_FILTERS]: answers['options-alert'] ? answers['options-alert'][0] : 'not set',
    }
  }

  return null
}

export const getEmailFromAnswers = (answers: Answers) => {
  return answers['email-address']
    ? answers['email-address'][0]
    : answers['email-address-lead']
    ? answers['email-address-lead'][0]
    : answers['email-address-optional']
    ? answers['email-address-optional'][0]
    : answers['email-address-newsletter']
    ? answers['email-address-newsletter'][0]
    : answers['email-address-optional-with-alert']
    ? answers['email-address-optional-with-alert'][0]
    : answers['email-address-ticket']
    ? answers['email-address-ticket'][0]
    : answers['email-address-email-collector']
    ? answers['email-address-email-collector'][0]
    : null
}

export const sendAlertRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const mapScenarioTypeAndAlertEndpointFragment: Partial<Record<Partial<ScenarioType>, string>> = {
    [ScenarioType.MOBILE]: 'mobile',
    [ScenarioType.MOBILE_ALERT]: 'mobile',
    [ScenarioType.MOBILE_ALERT_OBSERVER]: 'mobile',
    [ScenarioType.MOBILE_SEARCH_EMAIL]: 'mobile',
    [ScenarioType.INTERNET_ALERT]: 'internet',
    [ScenarioType.INTERNET_ALERT_OBSERVER]: 'internet',
    [ScenarioType.EMAIL_PAGE_GO_INTERNET]: 'internet',
    [ScenarioType.EMAIL_PAGE_GO_MOBILE]: 'mobile',
  }

  // const emailAddress = answers['email-address']![0] || null
  // const segmentRegisterEndpoint = `${getBaseUrl()}/api/register-to-newsletter/${
  //   mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
  // }/${emailAddress}`;

  let alertEndpoint: RequestInfo | URL = ''

  const payload = new FormData()
  const payloadRawObject = createAlertBodyRequest(answers, scenarioType)
  for (const i in payloadRawObject) {
    payload.append(i, payloadRawObject[i] as string)
  }

  if (scenarioType === ScenarioType.COMPETITION_INTERNET_ALERT) {
    alertEndpoint = `${getBaseUrl()}/api/handle-internet-contest-alert`
  } else {
    alertEndpoint = `${getBaseUrl()}/dyn/${getLocale()}/createAlertReact/${
      mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
    }`
  }

  const response = await fetch(alertEndpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    // mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })

  if (cancelRegister) {
    const endpoint = `${getBaseUrl()}/api/remove-user-from-mailer-segment`

    const payloadRawObject = {
      email: getEmailFromAnswers(answers),
      sid: 208,
    }

    fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(payloadRawObject),
      headers: {
        'Content-type': 'application/json',
      },
    })
  }

  let leadEventSouce: string | null = null

  if ((await response.json()).success) {
    const locale = getLocale()
    const nav = mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
    const typeProviderCookie = nav === 'mobile' ? 'Mobile' : 'Internet'

    let additionalInformation: Record<string, unknown> = {}

    if (getCookie('scenario') && JSON.parse(getCookie('scenario')).initialInformation) {
      additionalInformation = JSON.parse(getCookie('scenario').replace(/%3B/g, ';')).initialInformation
    }

    if (ScenarioType.EMAIL_PAGE_GO_INTERNET === scenarioType || ScenarioType.EMAIL_PAGE_GO_MOBILE === scenarioType) {
      if (getCookie('iigps')) {
        // initial info go page scenario
        const initialInfoGoPage = JSON.parse(atob(getCookie('iigps')))
        additionalInformation = restoreScenarioInitialInformation(initialInfoGoPage)
        setCookie('iigps', '')
      }
    }

    const searchCriteriaObject = getSearchCriteriaObject(nav, answers, additionalInformation)

    if (nav === 'mobile') {
      sendGA4Event('GA4-alert-create-mobile', {
        event_category: 'alert-create-mobile',
        application_version: 'not set',
        database_build: 'not set',
        region: getCookie('province') || 'not set',
        locale: locale,
        search_criteria: JSON.stringify(searchCriteriaObject) || 'not set',
        current_provider: getCookie(`current${typeProviderCookie}Provider`) || 'not set',
        url: window.location.href,
        source: getCookie('referrerUrl') || document.referrer || window.location.href,
        domain: 'not set',
        email: answers['email-address']![0] || 'not set',
      })

      if (ScenarioType.MOBILE_ALERT_OBSERVER === scenarioType) {
        leadEventSouce = 'Mobile-PriceDrop-Alert'
      } else if (ScenarioType.MOBILE_ALERT === scenarioType) {
        leadEventSouce = 'Mobile-Regular-Alert'
      }
    }

    if (nav === 'internet') {
      sendGA4Event('GA4-alert-create-internet', {
        event_category: 'alert-create-mobile',
        application_version: 'not set',
        database_build: 'not set',
        region: getCookie('province') || 'not set',
        locale: locale,
        search_criteria: JSON.stringify(searchCriteriaObject) || 'not set',
        current_provider: getCookie(`current${typeProviderCookie}Provider`) || 'not set',
        url: window.location.href,
        source: getCookie('referrerUrl') || document.referrer || window.location.href,
        domain: 'not set',
        email: answers['email-address']![0] || 'not set',
        postal_code: answers['postal-code-internet']![0] || 'not set',
      })

      if (ScenarioType.INTERNET_ALERT_OBSERVER === scenarioType) {
        leadEventSouce = 'Internet-PriceDrop-Alert'
      } else if (ScenarioType.INTERNET_ALERT === scenarioType) {
        leadEventSouce = 'Internet-Regular-Alert'
      }
    }
  }

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, leadEventSouce)
  }, 2000)
}

export const sendFiberOpticAlertRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const alertEndpoint: RequestInfo | URL = `${getBaseUrl()}/api/customer/save-attributes/fiber_alert`

  const payload = new FormData()
  const payloadRawObject = createFiberOpticAlertBodyRequest(answers, scenarioType)
  for (const i in payloadRawObject) {
    payload.append(i, payloadRawObject[i] as string)
  }

  await fetch(alertEndpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    // mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, 'fiber-alert')
  }, 2000)
}

export const sendUserGovernmentEmployeeInfoRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  const endpoint = `${getBaseUrl()}/api/customer/save-attributes/is_government_employee`

  const payloadRawObject = createUserGovernmentEmployeeInfoBodyRequest(answers, scenarioType)

  await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(payloadRawObject),
    headers: {
      'Content-type': 'application/json',
    },
  })
}

export const sendLeadRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  // const payload = new FormData()
  const payloadRawObject = createLeadBodyRequest(answers, scenarioType)
  // for (const i in payloadRawObject) {
  //   payload.append(i, payloadRawObject[i] as string)
  // }

  // const endpoint = `${getBaseUrl()}/dyn/${getLocale()}/createAlert/${
  //   mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
  // }`
  // const locale = getLocale()
  const endpoint = `${getBaseUrl()}/lead`
  let leadEventSouce: string | null = null

  if (scenarioType === ScenarioType.LEAD_MOBILE) {
    leadEventSouce = 'mobile-lead'
  } else if (scenarioType === ScenarioType.LEAD_INTERNET) {
    leadEventSouce = 'internet-lead'
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    // body: payload,
    body: JSON.stringify(payloadRawObject),
    // mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })

  if ((await response.json()).success) {
    const planId = (payloadRawObject['planId'] as string | undefined) || 'not set'
    const nav = payloadRawObject['nav'] as string
    const locale = getLocale()
    const lineItemId = (payloadRawObject['lineItemId'] as string | undefined) || 'not set'
    const campaignId = (payloadRawObject['campaignId'] as string | undefined) || 'not set'
    const bundleStatus = (payloadRawObject['bundle_status'] as string | undefined) || 'not set'

    const planInfo = () => {
      if (nav === 'mobile') {
        return {
          placeId: (payloadRawObject['placeId'] as string | undefined) || 'not set',
          planId: planId || 'not set',
          planMonthlyPrice: (payloadRawObject['planMonthlyPrice'] as string | undefined) || 'not set',
          planName: (payloadRawObject['planName'] as string | undefined) || 'not set',
          planUpfrontPrice: (payloadRawObject['planUpfrontPrice'] as string | undefined) || 'not set',
        }
      }

      if (nav === 'internet') {
        return {
          placeId: (payloadRawObject['placeId'] as string | undefined) || 'not set',
          planDataUsage: (payloadRawObject['planDataUsage'] as string | undefined) || 'not set',
          planId: planId || 'not set',
          planMonthlyPrice: (payloadRawObject['planMonthlyPrice'] as string | undefined) || 'not set',
          planName: (payloadRawObject['planName'] as string | undefined) || 'not set',
          planUpfrontPrice: (payloadRawObject['planUpfrontPrice'] as string | undefined) || 'not set',
        }
      }
    }

    // const cplType = nav === 'internet' ? 'internetTS' : 'mobileTS'
    // console.log('cplType: ' + cplType)
    // const cpl = (payloadRawObject['internetTS'] as number) || 'not set'
    const providerName = (payloadRawObject['providerSlug'] as string) || 'not set'

    // type EventProperties = Record<string, any>

    // const eventProperties: EventProperties = {
    //   currency: 'CAD',
    //   transation_id: Date.now(),
    //   value: payloadRawObject['tl'] || 'not set', //(payloadRawObject[cplType] as number) || 'not set',
    //   items: [
    //     {
    //       item_id: planId,
    //       locale: locale,
    //       item_category: payloadRawObject['nav'],
    //       item_category2: 'nativeCTA',
    //       item_category3: getCookie('pageRoute') || 'question',
    //       item_category4: 'cpl',
    //       item_category5: payloadRawObject['tl'] || 'not set',
    //       item_brand: providerName,
    //       price: payloadRawObject['tl'] || 'not set', // (payloadRawObject[cplType] as number) || 'not set',
    //       planInfo: planInfo(), // on verra si ca fonctionne
    //       affiliation: 'questionnaire',
    //       line_item_id: lineItemId,
    //       campaign_id: campaignId,
    //       bundle_status: bundleStatus,
    //     },
    //   ],
    // }

    // setTimeout(function sendGoogleAnalyticsEvent() {
    //   window.analytics.track('purchase', { eventProperties })
    // }, 1)

    // sendGA4Event('purchase', {
    //   currency: 'CAD',
    //   transation_id: Date.now(),
    //   value: (payloadRawObject['tl'] as number) || 'not set', //(payloadRawObject[cplType] as number) || 'not set',
    //   items: [
    //     {
    //       item_id: providerName + `_${payloadRawObject['nav'] || 'internet'}-native-click_0`,
    //       locale: locale || 'not set',
    //       item_category: payloadRawObject['nav'] || 'not set',
    //       item_category2: 'nativeCTA',
    //       item_category3: getCookie('pageRoute') || 'question',
    //       item_category4: 'cpl',
    //       item_category5: payloadRawObject['tl'] || 'not set',
    //       item_brand: providerName || 'not set',
    //       price: (payloadRawObject['tl'] as number) || 'not set', // (payloadRawObject[cplType] as number) || 'not set',
    //       planInfo: planInfo(), // on verra si ca fonctionne
    //       affiliation: 'questionnaire',
    //       line_item_id: lineItemId || 'not set',
    //       campaign_id: campaignId || 'not set',
    //       bundle_status: bundleStatus || 'not set',
    //     },
    //   ],
    // })

    const purchaseBackendEndpoint = `https://www.planhub.ca/api/lead/purchase`
    const DEFAULT_ID = 'default_id_' + Math.random().toString(36).substring(2, 15)
    const clientId = getCookie('cio_id') || DEFAULT_ID
    const currentDomain = (hostname: string) => {
      if (hostname.includes('mobilesyrup.planhub.ca')) {
        return 'mobilesyrup'
      } else if (hostname.includes('ratehub.planhub.ca')) {
        return 'ratehub'
      } else if (hostname.includes('francoischarron.planhub.ca')) {
        return 'francoischarron'
      } else if (hostname.includes('protegezvous.planhub.ca')) {
        return 'protegezvous'
      } else if (hostname.includes('lactualite.planhub.ca')) {
        return 'lactualite'
      } else if (hostname.includes('redflagdeals.planhub.ca')) {
        return 'redflagdeals'
      } else {
        return 'default'
      }
    }

    const hostname = window.location.hostname

    const purchasePayload = {
      clientId: clientId,
      locale: locale,
      CPL: payloadRawObject['tl'] || 'not set',
      provider: providerName,
      event_category: 'internet-native-click',
      pageRoute: getCookie('pageRoute') || 'question',
      nav: payloadRawObject['nav'] || 'mobile',
      affiliation: 'questionnaire',
      lineItemId: lineItemId || 'not set',
      campaignId: campaignId || 'not set',
      bundleStatus: bundleStatus || 'not set',
      domain: currentDomain(hostname) || 'not set',
      adblock: false,
      region: getCookie('province') || 'quebec',
      fbid: 'not set',
      planInfo: JSON.stringify(planInfo()),
    }

    if (!isDev()) {
      await fetch(purchaseBackendEndpoint, {
        method: 'POST',
        body: JSON.stringify(purchasePayload),
        headers: {
          'Content-type': 'application/json',
        },
      })
    }
  }

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, leadEventSouce)
  }, 2000)
}

export const sendMektelLeadRequest = async (answers: Answers, scenarioType: Partial<ScenarioType>) => {
  // const payload = new FormData()
  const payloadRawObject = createMektelLeadBodyRequest(answers, scenarioType)
  // for (const i in payloadRawObject) {
  //   payload.append(i, payloadRawObject[i] as string)
  // }

  // const endpoint = `${getBaseUrl()}/dyn/${getLocale()}/createAlert/${
  //   mapScenarioTypeAndAlertEndpointFragment[scenarioType as Partial<ScenarioType>]
  // }`
  // const locale = getLocale()
  const endpoint = `${getBaseUrl()}/lead`
  let leadEventSouce: string | null = null

  if (scenarioType === ScenarioType.LEAD_MOBILE) {
    leadEventSouce = 'mobile-lead'
  } else if (scenarioType === ScenarioType.LEAD_INTERNET) {
    leadEventSouce = 'internet-lead'
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    // body: payload,
    body: JSON.stringify(payloadRawObject),
    // mode: 'no-cors',
    headers: {
      'Content-type': 'application/json',
    },
  })

  if ((await response.json()).success) {
    const planId = (payloadRawObject['planId'] as string | undefined) || 'not set'
    const nav = payloadRawObject['nav'] as string
    const locale = getLocale()
    const lineItemId = (payloadRawObject['lineItemId'] as string | undefined) || 'not set'
    const campaignId = (payloadRawObject['campaignId'] as string | undefined) || 'not set'
    const bundleStatus = (payloadRawObject['bundle_status'] as string | undefined) || 'not set'

    const planInfo = () => {
      if (nav === 'mobile') {
        return {
          placeId: (payloadRawObject['placeId'] as string | undefined) || 'not set',
          planId: planId || 'not set',
          planMonthlyPrice: (payloadRawObject['planMonthlyPrice'] as string | undefined) || 'not set',
          planName: (payloadRawObject['planName'] as string | undefined) || 'not set',
          planUpfrontPrice: (payloadRawObject['planUpfrontPrice'] as string | undefined) || 'not set',
        }
      }

      if (nav === 'internet') {
        return {
          placeId: (payloadRawObject['placeId'] as string | undefined) || 'not set',
          planDataUsage: (payloadRawObject['planDataUsage'] as string | undefined) || 'not set',
          planId: planId || 'not set',
          planMonthlyPrice: (payloadRawObject['planMonthlyPrice'] as string | undefined) || 'not set',
          planName: (payloadRawObject['planName'] as string | undefined) || 'not set',
          planUpfrontPrice: (payloadRawObject['planUpfrontPrice'] as string | undefined) || 'not set',
        }
      }
    }

    // const cplType = nav === 'internet' ? 'internetTS' : 'mobileTS'
    // console.log('cplType: ' + cplType)
    // const cpl = (payloadRawObject['internetTS'] as number) || 'not set'
    const providerName = (payloadRawObject['providerSlug'] as string) || 'not set'

    // type EventProperties = Record<string, any>

    // const eventProperties: EventProperties = {
    //   currency: 'CAD',
    //   transation_id: Date.now(),
    //   value: payloadRawObject['tl'] || 'not set', //(payloadRawObject[cplType] as number) || 'not set',
    //   items: [
    //     {
    //       item_id: planId,
    //       locale: locale,
    //       item_category: payloadRawObject['nav'],
    //       item_category2: 'nativeCTA',
    //       item_category3: getCookie('pageRoute') || 'question',
    //       item_category4: 'cpl',
    //       item_category5: payloadRawObject['tl'] || 'not set',
    //       item_brand: providerName,
    //       price: payloadRawObject['tl'] || 'not set', // (payloadRawObject[cplType] as number) || 'not set',
    //       planInfo: planInfo(), // on verra si ca fonctionne
    //       affiliation: 'questionnaire',
    //       line_item_id: lineItemId,
    //       campaign_id: campaignId,
    //       bundle_status: bundleStatus,
    //     },
    //   ],
    // }

    // setTimeout(function sendGoogleAnalyticsEvent() {
    //   window.analytics.track('purchase', { eventProperties })
    // }, 1)

    // sendGA4Event('purchase', {
    //   currency: 'CAD',
    //   transation_id: Date.now(),
    //   value: (payloadRawObject['tl'] as number) || 'not set', //(payloadRawObject[cplType] as number) || 'not set',
    //   items: [
    //     {
    //       item_id: providerName + `_${payloadRawObject['nav'] || 'internet'}-native-click_0`,
    //       locale: locale || 'not set',
    //       item_category: payloadRawObject['nav'] || 'not set',
    //       item_category2: 'nativeCTA',
    //       item_category3: getCookie('pageRoute') || 'question',
    //       item_category4: 'cpl',
    //       item_category5: payloadRawObject['tl'] || 'not set',
    //       item_brand: providerName || 'not set',
    //       price: (payloadRawObject['tl'] as number) || 'not set', // (payloadRawObject[cplType] as number) || 'not set',
    //       planInfo: planInfo(), // on verra si ca fonctionne
    //       affiliation: 'questionnaire',
    //       line_item_id: lineItemId || 'not set',
    //       campaign_id: campaignId || 'not set',
    //       bundle_status: bundleStatus || 'not set',
    //     },
    //   ],
    // })

    const purchaseBackendEndpoint = `https://www.planhub.ca/api/lead/purchase`
    const DEFAULT_ID = 'default_id_' + Math.random().toString(36).substring(2, 15)
    const clientId = getCookie('cio_id') || DEFAULT_ID
    const currentDomain = (hostname: string) => {
      if (hostname.includes('mobilesyrup.planhub.ca')) {
        return 'mobilesyrup'
      } else if (hostname.includes('ratehub.planhub.ca')) {
        return 'ratehub'
      } else if (hostname.includes('francoischarron.planhub.ca')) {
        return 'francoischarron'
      } else if (hostname.includes('protegezvous.planhub.ca')) {
        return 'protegezvous'
      } else if (hostname.includes('lactualite.planhub.ca')) {
        return 'lactualite'
      } else if (hostname.includes('redflagdeals.planhub.ca')) {
        return 'redflagdeals'
      } else {
        return 'default'
      }
    }

    const hostname = window.location.hostname

    const purchasePayload = {
      clientId: clientId,
      locale: locale,
      CPL: payloadRawObject['tl'] || 'not set',
      provider: providerName,
      event_category: 'internet-native-click',
      pageRoute: getCookie('pageRoute') || 'question',
      nav: payloadRawObject['nav'] || 'mobile',
      affiliation: 'questionnaire',
      lineItemId: lineItemId || 'not set',
      campaignId: campaignId || 'not set',
      bundleStatus: bundleStatus || 'not set',
      domain: currentDomain(hostname) || 'not set',
      adblock: false,
      region: getCookie('province') || 'quebec',
      fbid: 'not set',
      planInfo: JSON.stringify(planInfo()),
    }

    if (!isDev()) {
      await fetch(purchaseBackendEndpoint, {
        method: 'POST',
        body: JSON.stringify(purchasePayload),
        headers: {
          'Content-type': 'application/json',
        },
      })
    }
  }

  setTimeout(() => {
    const email = getEmailFromAnswers(answers)
    identifyCioUser(email, leadEventSouce)
  }, 2000)
}

const isDev = () => {
  const devEnvironments = ['localhost', 'dev01', 'dev02', 'dev03', 'dev04', 'dev05', 'local.', 'website.']

  return devEnvironments.some((env) => window.location.hostname.includes(env))
}

export const mapBundleStatus = (bundleStatusCode: any) => {
  const mapBundleStatus: { [key: number]: string } = {
    0: 'none',
    1: 'alone',
    2: 'bundle',
  }

  if (Object.prototype.hasOwnProperty.call(mapBundleStatus, bundleStatusCode)) {
    return mapBundleStatus[bundleStatusCode]
  } else {
    return 'not set'
  }
}

export const checkProviderAvailability = async ({
  providerSlug,
  planId,
  postalCode,
}: {
  providerSlug: string
  planId: string
  postalCode: string
}): Promise<Array<string>> => {
  const endpoint = `${getBaseUrl()}/home-internet/modal/is-provider-available/${providerSlug}?dyn=1`
  const placeId = getCookie('placeId')

  const response = await fetch(endpoint, {
    method: 'POST',
    body: JSON.stringify({ placeId, planId, postalCode }),
    headers: {
      'Content-type': 'application/json',
    },
  })

  return (await response.json()).deal.toString()
}
